import enTranslations from '../locales/en.json';

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
  es: { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  de: { name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  pt: { name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  ar: { name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  zh: { name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  ja: { name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  ko: { name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  th: { name: 'Thai', nativeName: 'ภาษาไทย', flag: '🇹🇭' },
};

const DEFAULT_LANGUAGE = 'en';

class TranslateService {
  constructor() {
    this.currentLanguage = DEFAULT_LANGUAGE;
    this.isInitialized = false;
    this.listeners = [];
    this.translations = { en: enTranslations };
    this.translationsLoaded = true;
    this.translationsEnabled = true;
    this.enabledLanguages = Object.keys(SUPPORTED_LANGUAGES);
    this._loadingLanguages = new Set();
  }

  init() {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('preferred_language') : null;
    const browser = typeof navigator !== 'undefined' ? navigator.language?.split('-')[0] : null;
    const lang =
      (saved && SUPPORTED_LANGUAGES[saved]) ? saved :
      (browser && SUPPORTED_LANGUAGES[browser]) ? browser :
      DEFAULT_LANGUAGE;

    this.currentLanguage = lang;
    this.isInitialized = true;

    if (lang !== DEFAULT_LANGUAGE) {
      this._loadLanguagePack(lang);
    }

    this.updateDocumentDirection();
    return lang;
  }

  async _loadLanguagePack(langCode) {
    if (this.translations[langCode] || this._loadingLanguages.has(langCode)) return;
    this._loadingLanguages.add(langCode);
    try {
      let mod;
      switch (langCode) {
        case 'es': mod = await import('../locales/es.json'); break;
        case 'fr': mod = await import('../locales/fr.json'); break;
        case 'de': mod = await import('../locales/de.json'); break;
        case 'pt': mod = await import('../locales/pt.json'); break;
        case 'hi': mod = await import('../locales/hi.json'); break;
        case 'ar': mod = await import('../locales/ar.json'); break;
        case 'zh': mod = await import('../locales/zh.json'); break;
        case 'ja': mod = await import('../locales/ja.json'); break;
        case 'ko': mod = await import('../locales/ko.json'); break;
        case 'th': mod = await import('../locales/th.json'); break;
        default: mod = null;
      }
      if (mod) {
        this.translations[langCode] = mod.default || mod;
        this.notifyListeners();
      }
    } catch {
      this.translations[langCode] = {};
    } finally {
      this._loadingLanguages.delete(langCode);
    }
  }

  async setLanguage(langCode) {
    if (!SUPPORTED_LANGUAGES[langCode]) return false;

    if (langCode !== DEFAULT_LANGUAGE && !this.translations[langCode]) {
      await this._loadLanguagePack(langCode);
    }

    this.currentLanguage = langCode;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('preferred_language', langCode);
    }
    this.updateDocumentDirection();
    this.notifyListeners();
    return true;
  }

  isRTL(lang) {
    return RTL_LANGUAGES.includes(lang || this.currentLanguage);
  }

  t(key, params = {}) {
    if (typeof key !== 'string') return '';
    const lookup = (obj) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
    const langPack = this.translations[this.currentLanguage];
    const value = (langPack && lookup(langPack)) || lookup(enTranslations);
    if (typeof value !== 'string') return key;
    return this.interpolate(value, params);
  }

  interpolate(str, params) {
    return str.replace(/\{\{(\w+)\}\}/g, (match, k) =>
      params[k] !== undefined ? params[k] : match
    );
  }

  getLanguage() { return this.currentLanguage; }

  getLanguageInfo(lang) {
    return SUPPORTED_LANGUAGES[lang || this.currentLanguage];
  }

  getEnabledLanguages() { return SUPPORTED_LANGUAGES; }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach((cb) => {
      try { cb(this.currentLanguage); } catch { /* swallow */ }
    });
  }

  updateDocumentDirection() {
    if (typeof document === 'undefined') return;
    const rtl = this.isRTL();
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = this.currentLanguage;
  }

  getAllTranslations() { return enTranslations; }
  loadAdminSettings() { return Promise.resolve(); }
  loadLanguagePack(code) { return this._loadLanguagePack(code); }
}

const translateService = new TranslateService();
const t = (key, params) => translateService.t(key, params);

export default translateService;
export { TranslateService, t };
